import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { parseCv } from '../../../services/cvParser';
import { matchProfile, getRole } from '../../../services/skillMatcher';
import { ROLES, SKILL_BY_ID, labelFor } from '../../../data/skillOntology';
import LearningRecommendations from './LearningRecommendations';
import FreeCoursesPanel from './FreeCoursesPanel';
import { enrichSkillsWithLlm, isLlmEnricherAvailable } from '../../../services/aiSkillEnricher';
import { proposeCvUpload } from '../../../services/approvalStore';
import { getTenantConfig } from '../../../services/tenantConfigStore';
import { localiseJustification } from '../../../data/adjacencyTranslations';

/**
 * MyProfile, the "My Profile" tab on the employee portal.
 *
 * Real CV parsing (PDF / DOCX / TXT, fully client-side), skill matching
 * against the multilingual skill ontology, and EU AI Act transparency
 * disclosure on every AI-influenced output (Art. 13).
 *
 * Persistence: parsed profile saves to localStorage per employee ID.
 * GDPR posture: no file ever leaves the browser.
 */

const STORAGE_KEY_BASE = 'clearequal.profile.v1';

// Map i18n language codes to the 5 we support in the ontology
const SUPPORTED = ['en', 'de', 'fr', 'es', 'it'];
const normaliseLang = (lng) => {
  const base = (lng || 'en').toLowerCase().split('-')[0];
  return SUPPORTED.includes(base) ? base : 'en';
};

const MyProfile = () => {
  const { t, i18n } = useTranslation();
  const lang = normaliseLang(i18n.language);
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [parseError, setParseError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedRoleKey, setSelectedRoleKey] = useState(null);

  // Per-user storage key
  const storageKey = useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      const u = raw ? JSON.parse(raw) : null;
      return `${STORAGE_KEY_BASE}.${u?.employeeId || u?.email || 'anon'}`;
    } catch {
      return `${STORAGE_KEY_BASE}.anon`;
    }
  }, []);

  // Hydrate from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const restored = JSON.parse(raw);
        setProfile(restored);
        setSelectedRoleKey(restored.inferredRoleKey || 'seniorDirectorEnterprise');
      }
    } catch {}
  }, [storageKey]);

  const persistProfile = useCallback((p) => {
    try {
      if (p) localStorage.setItem(storageKey, JSON.stringify(p));
      else localStorage.removeItem(storageKey);
    } catch {}
  }, [storageKey]);

  // LLM enrichment state, UI shows a "AI is reviewing your CV..." badge
  // while the call is in flight, and an "AI added X skills" badge when done.
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichmentInfo, setEnrichmentInfo] = useState(null);
  const [pendingCv, setPendingCv] = useState(null); // set when a CV upload awaits approval
  const llmEnricherAvailable = isLlmEnricherAvailable();

  const handleFile = useCallback(async (file) => {
    setParseError('');
    setIsParsing(true);
    setEnrichmentInfo(null);
    try {
      const parsed = await parseCv(file);

      // Per-tenant gate: if CV uploads must be approved, do NOT apply the CV to
      // the profile or skill match. Create a pending approval (routed to the
      // configured approver: works council rep or manager) and show a pending
      // state. The profile only updates once an approver approves it.
      // NOTE (step two): learning recommendations and the pay-gap explanation
      // will be decoupled from the CV so they still render while pending.
      const cfg = getTenantConfig('current');
      if (cfg?.approvals?.cvUploadGated) {
        proposeCvUpload({
          tenantId: 'current',
          employeeId: parsed.email || 'self',
          employeeName: parsed.name || parsed.email || 'employee',
          proposedBy: parsed.email || 'self',
          parsedCv: parsed,
        });
        setPendingCv({
          name: parsed.name || parsed.email || 'your CV',
          filename: parsed.filename || 'CV',
          at: Date.now(),
        });
        setIsParsing(false);
        return; // nothing is applied until an approver approves it
      }

      setProfile(parsed);
      setSelectedRoleKey(parsed.inferredRoleKey || 'seniorDirectorEnterprise');
      persistProfile(parsed);

      // Optional LLM enrichment, only fires if VITE_ANTHROPIC_API_KEY is set.
      // Non-blocking: the user already sees their parsed profile by now;
      // the enrichment merges in additional skills the keyword/title parser
      // missed when it returns. Constrained output (skill IDs from a fixed
      // enum) means there's no fabrication risk.
      if (isLlmEnricherAvailable()) {
        setIsEnriching(true);
        enrichSkillsWithLlm(parsed)
          .then((res) => {
            if (res?.enrichedSkillIds?.length > 0) {
              const merged = {
                ...parsed,
                detectedSkillIds: Array.from(new Set([
                  ...parsed.detectedSkillIds,
                  ...res.enrichedSkillIds,
                ])),
                llmEnrichedSkillIds: res.enrichedSkillIds,
                llmRationale: res.rationale,
              };
              setProfile(merged);
              persistProfile(merged);
              setEnrichmentInfo({
                addedCount: res.enrichedSkillIds.length,
                rationale: res.rationale,
              });
            } else {
              setEnrichmentInfo({ addedCount: 0, rationale: res?.rationale || '' });
            }
          })
          .finally(() => setIsEnriching(false));
      }
    } catch (err) {
      console.error('CV parse failed', err);
      setParseError(err?.message || t('employeePortal.profile.parseError'));
    } finally {
      setIsParsing(false);
    }
  }, [persistProfile, t]);

  const onChooseFile = () => fileInputRef.current?.click();
  const onFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };
  const handleClearProfile = () => {
    setProfile(null);
    setSelectedRoleKey(null);
    setParseError('');
    persistProfile(null);
  };

  const role = selectedRoleKey ? getRole(selectedRoleKey) : null;
  const match = useMemo(() => {
    if (!profile || !role) return null;
    // Silent: prevents audit-log spam on every re-render
    return matchProfile(profile.detectedSkillIds, role, { silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, role?.id]);

  // Skill pill component, uses multilingual label
  const SkillPill = ({ skillId, variant }) => {
    const skill = SKILL_BY_ID[skillId];
    if (!skill) return null;
    const variantClasses = {
      strength: 'bg-success/10 text-success border-success/20',
      missing: 'bg-warning/10 text-warning border-warning/30',
      bonus: 'bg-primary/10 text-primary border-primary/20',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border ${variantClasses[variant]}`}>
        {variant === 'strength' && <Icon name="Check" size={12} />}
        {variant === 'missing' && <Icon name="Circle" size={12} />}
        {variant === 'bonus' && <Icon name="Plus" size={12} />}
        {labelFor(skill, lang)}
      </span>
    );
  };

  // ─── Empty state ────────────────────────────────────────────────────
  if (!profile && !isParsing) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-1">{t('employeePortal.profile.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('employeePortal.profile.subtitle')}</p>
        </div>

        {/* Pending verification: CV uploaded, awaiting approver (works council rep or manager) */}
        {pendingCv && (
          <div className="bg-warning/10 border-l-4 border-warning p-4 rounded">
            <div className="flex items-start gap-3">
              <Icon name="Clock" size={20} className="text-warning shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-foreground">
                  {pendingCv.filename} submitted for verification
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your CV has been sent to an approver (your works council representative or manager) before it counts toward your skill match. This protects the integrity of pay-equity data. You will see your updated skill match once it is approved.
                </p>
              </div>
            </div>
          </div>
        )}

        {pendingCv && (
          <div className="space-y-4">
            <select value={selectedRoleKey || ''} onChange={(e)=>setSelectedRoleKey(e.target.value||null)} className="block w-full rounded-md border border-border bg-card px-3 py-2 text-sm">
              <option value="">Select your role...</option>
              {Object.keys(ROLES).map((k)=>(<option key={k} value={k}>{labelFor(ROLES[k],lang)}</option>))}
            </select>
            {selectedRoleKey && ROLES[selectedRoleKey]?.requiredSkillIds && (<LearningRecommendations missingSkillIds={ROLES[selectedRoleKey].requiredSkillIds} lang={lang} />)}
          </div>
        )}

        {/* Article 7 framing */}
        <div className="bg-primary/5 border-l-4 border-primary p-4 rounded">
          <div className="flex items-start gap-3">
            <Icon name="Scale" size={20} className="text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm text-foreground">{t('employeePortal.profile.consentBox')}</p>
              <p className="text-sm text-muted-foreground mt-1">{t('employeePortal.profile.consentBody')}</p>
            </div>
          </div>
        </div>

        {/* Upload zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={onDrop}
          onClick={onChooseFile}
          className={`bg-card border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
            isDragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
          }`}
        >
          <div className="w-14 h-14 rounded-full bg-primary/10 mx-auto flex items-center justify-center mb-4">
            <Icon name="UploadCloud" size={28} className="text-primary" />
          </div>
          <p className="font-semibold text-foreground mb-1">{t('employeePortal.profile.uploadTitle')}</p>
          <p className="text-sm text-muted-foreground mb-5">{t('employeePortal.profile.uploadHint')}</p>
          <Button variant="outline" size="sm" iconName="FileText" iconPosition="left">
            {t('employeePortal.profile.uploadCta')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            onChange={onFileInputChange}
            className="hidden"
          />
          <p className="text-xs text-muted-foreground mt-5 flex items-center justify-center gap-1.5">
            <Icon name="Lock" size={12} />
            {t('employeePortal.profile.gdprNote')}
          </p>
        </div>

        {parseError && (
          <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm text-destructive flex items-start gap-2">
            <Icon name="AlertCircle" size={16} className="shrink-0 mt-0.5" />
            <span>{parseError}</span>
          </div>
        )}
      </div>
    );
  }

  // ─── Parsing state ──────────────────────────────────────────────────
  if (isParsing) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 mx-auto flex items-center justify-center mb-4">
          <Icon name="Loader" size={28} className="text-primary animate-spin" />
        </div>
        <p className="font-semibold text-foreground mb-1">{t('employeePortal.profile.parsingTitle')}</p>
        <p className="text-sm text-muted-foreground">{t('employeePortal.profile.parsingBody')}</p>
      </div>
    );
  }

  // ─── Results state ──────────────────────────────────────────────────
  const scoreLabel = (() => {
    if (!match) return '';
    if (match.scorePercent >= 85) return t('employeePortal.profile.matchPerfect');
    if (match.scorePercent >= 65) return t('employeePortal.profile.matchStrong');
    if (match.scorePercent >= 40) return t('employeePortal.profile.matchPartial');
    return t('employeePortal.profile.matchEarly');
  })();

  const scoreColor = (() => {
    if (!match) return 'text-muted-foreground';
    if (match.scorePercent >= 65) return 'text-success';
    if (match.scorePercent >= 40) return 'text-warning';
    return 'text-destructive';
  })();

  return (
    <div className="space-y-6">
      {/* AI Act Article 13 (Transparency) banner, disclosed BEFORE results */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
        <Icon name="Cpu" size={18} className="text-blue-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-xs font-semibold text-blue-900">{t('employeePortal.profile.aiTransparencyTitle')}</p>
          <p className="text-xs text-blue-800 mt-0.5">{t('employeePortal.profile.aiTransparencyBody')}</p>
        </div>
      </div>

      {/* LLM enrichment status, only when key is configured */}
      {llmEnricherAvailable && isEnriching && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-center gap-3">
          <Icon name="Loader" size={16} className="text-primary animate-spin shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-foreground">{t('employeePortal.profile.aiEnrichmentLoading')}</p>
            <p className="text-xs text-muted-foreground">{t('employeePortal.profile.aiEnrichmentLoadingBody')}</p>
          </div>
        </div>
      )}
      {llmEnricherAvailable && !isEnriching && enrichmentInfo && enrichmentInfo.addedCount > 0 && (
        <div className="bg-success/5 border border-success/20 rounded-lg p-3 flex items-start gap-3">
          <Icon name="Sparkles" size={16} className="text-success shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-foreground">
              {t('employeePortal.profile.aiEnrichmentDone', { count: enrichmentInfo.addedCount })}
            </p>
            {enrichmentInfo.rationale && (
              <p className="text-xs text-muted-foreground mt-0.5">"{enrichmentInfo.rationale}"</p>
            )}
          </div>
        </div>
      )}

      {/* Profile header */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="User" size={26} className="text-primary" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                {t('employeePortal.profile.profileHeader')}
              </p>
              <h2 className="text-xl font-bold text-foreground">{profile.name || 'N/A'}</h2>
              {profile.currentTitle && (
                <p className="text-sm text-muted-foreground">{profile.currentTitle}</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" iconName="Upload" iconPosition="left" onClick={onChooseFile}>
              {t('employeePortal.profile.reupload')}
            </Button>
            <Button variant="ghost" size="sm" iconName="Trash2" iconPosition="left" onClick={handleClearProfile}>
              {t('employeePortal.profile.clearProfile')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              onChange={onFileInputChange}
              className="hidden"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border">
          {profile.email && (
            <div>
              <p className="text-xs text-muted-foreground">{t('employeePortal.profile.fieldEmail')}</p>
              <p className="text-sm font-medium text-foreground truncate">{profile.email}</p>
            </div>
          )}
          {profile.phone && (
            <div>
              <p className="text-xs text-muted-foreground">{t('employeePortal.profile.fieldPhone')}</p>
              <p className="text-sm font-medium text-foreground">{profile.phone}</p>
            </div>
          )}
          {profile.yearsExperience > 0 && (
            <div>
              <p className="text-xs text-muted-foreground">{t('employeePortal.profile.fieldYearsExp')}</p>
              <p className="text-sm font-medium text-foreground">
                {profile.yearsExperience} {t('employeePortal.profile.fieldYearsExpUnit')}
              </p>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground">{t('employeePortal.profile.fieldFile')}</p>
            <p className="text-sm font-medium text-foreground truncate">{profile.filename}</p>
          </div>
        </div>
      </div>

      {/* Skill match */}
      {role && match && (
        <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-lg font-semibold text-foreground">{t('employeePortal.profile.matchTitle')}</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                {t('employeePortal.profile.matchAgainst')}: <span className="font-medium text-foreground">{labelFor(role, lang)}</span>
              </p>
            </div>
            <div className="text-right">
              <p className={`text-3xl font-bold ${scoreColor}`}>{match.scorePercent}%</p>
              <p className="text-xs text-muted-foreground">{scoreLabel}</p>
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              {t('employeePortal.profile.changeRole')}
            </label>
            <select
              value={selectedRoleKey || ''}
              onChange={(e) => setSelectedRoleKey(e.target.value)}
              className="w-full md:w-auto px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {Object.values(ROLES).map((r) => (
                <option key={r.id} value={r.id}>
                  {labelFor(r, lang)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="bg-success/5 border border-success/20 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="CheckCircle2" size={16} className="text-success" />
                <p className="font-semibold text-sm text-foreground">{t('employeePortal.profile.colStrengths')}</p>
                <span className="ml-auto text-xs text-muted-foreground">{match.matched.length}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{t('employeePortal.profile.colStrengthsBody')}</p>
              <div className="flex flex-wrap gap-1.5">
                {match.matched.length > 0
                  ? match.matched.map((id) => <SkillPill key={id} skillId={id} variant="strength" />)
                  : <span className="text-xs text-muted-foreground italic">{t('employeePortal.profile.noSkillsHere')}</span>}
              </div>
            </div>

            {/* Adjacent matches, required skill the user covers via a related skill */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="GitMerge" size={16} className="text-teal-600" />
                <p className="font-semibold text-sm text-foreground">{t('employeePortal.profile.colAdjacent')}</p>
                <span className="ml-auto text-xs text-muted-foreground">{match.adjacent.length}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{t('employeePortal.profile.colAdjacentBody')}</p>
              <div className="space-y-2">
                {match.adjacent.length > 0
                  ? match.adjacent.map((a) => {
                      const targetSkill = SKILL_BY_ID[a.skillId];
                      const viaSkill    = SKILL_BY_ID[a.via];
                      if (!targetSkill || !viaSkill) return null;
                      return (
                        <div key={a.skillId} className="bg-white border border-teal-200 rounded p-2.5">
                          <div className="flex items-center gap-1.5 flex-wrap text-xs">
                            <span className="font-medium text-foreground">{labelFor(targetSkill, lang)}</span>
                            <Icon name="ArrowLeft" size={12} className="text-teal-600" />
                            <span className="text-teal-700 font-medium">{labelFor(viaSkill, lang)}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{localiseJustification(a.skillId, a.via, a.justification, lang)}</p>
                        </div>
                      );
                    })
                  : <span className="text-xs text-muted-foreground italic">{t('employeePortal.profile.noSkillsHere')}</span>}
              </div>
            </div>

            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Target" size={16} className="text-warning" />
                <p className="font-semibold text-sm text-foreground">{t('employeePortal.profile.colGrowth')}</p>
                <span className="ml-auto text-xs text-muted-foreground">{match.missing.length}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{t('employeePortal.profile.colGrowthBody')}</p>
              <div className="flex flex-wrap gap-1.5">
                {match.missing.length > 0
                  ? match.missing.map((id) => <SkillPill key={id} skillId={id} variant="missing" />)
                  : <span className="text-xs text-muted-foreground italic">{t('employeePortal.profile.noSkillsHere')}</span>}
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Sparkles" size={16} className="text-primary" />
                <p className="font-semibold text-sm text-foreground">{t('employeePortal.profile.colBonus')}</p>
                <span className="ml-auto text-xs text-muted-foreground">{match.bonus.length}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{t('employeePortal.profile.colBonusBody')}</p>
              <div className="flex flex-wrap gap-1.5">
                {match.bonus.length > 0
                  ? match.bonus.map((id) => <SkillPill key={id} skillId={id} variant="bonus" />)
                  : <span className="text-xs text-muted-foreground italic">{t('employeePortal.profile.noSkillsHere')}</span>}
              </div>
            </div>
          </div>

          {/* Learning recommendations for Growth Areas */}
          {match.missing.length > 0 && (
            <LearningRecommendations missingSkillIds={match.missing} lang={lang} />
          )}

          {/* Free public courses, provider deep-links + LLM-suggested queries */}
          {match.missing.length > 0 && (
            <FreeCoursesPanel missingSkillIds={match.missing} lang={lang} />
          )}

          {/* AI Act Article 14 (Human Oversight) footer */}
          <div className="mt-5 pt-4 border-t border-border flex items-start gap-2 text-xs text-muted-foreground">
            <Icon name="UserCheck" size={14} className="text-muted-foreground shrink-0 mt-0.5" />
            <span>{t('employeePortal.profile.humanOversightNote')}</span>
          </div>
        </div>
      )}

      {parseError && (
        <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm text-destructive flex items-start gap-2">
          <Icon name="AlertCircle" size={16} className="shrink-0 mt-0.5" />
          <span>{parseError}</span>
        </div>
      )}
    </div>
  );
};

export default MyProfile;
